import React, { useState } from 'react';
import '../styles/contactForm.css';
import config from '../config';

export default function ContactForm({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [captcha, setCaptcha] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [emailError, setEmailError] = useState('');
  
  // Simple math captcha
  React.useEffect(() => {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    setCaptcha(`${num1} + ${num2}`);
    setCaptchaAnswer((num1 + num2).toString());
  }, [isOpen]);

  const validateEmail = (email) => {
    if (!email) {
      setEmailError('');
      return true;
    }
    
    // Email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    
    // Check for common typos in domain
    const commonDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'icloud.com'];
    const domain = email.split('@')[1]?.toLowerCase();
    
    if (domain) {
      // Check for single character typos
      const typos = {
        'gmial.com': 'gmail.com',
        'gmai.com': 'gmail.com',
        'yahooo.com': 'yahoo.com',
        'yaho.com': 'yahoo.com',
        'hotmial.com': 'hotmail.com',
        'outlok.com': 'outlook.com'
      };
      
      if (typos[domain]) {
        setEmailError(`Did you mean ${typos[domain]}?`);
        return false;
      }
    }
    
    setEmailError('');
    return true;
  };

  const validatePhoneNumber = (phone) => {
    if (!phone) {
      setPhoneError('');
      return true; // Phone is optional
    }
    
    // Remove spaces, hyphens, and parentheses
    const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
    
    // Check if it contains a country code (starts with +)
    const hasCountryCode = cleanPhone.startsWith('+');
    
    if (hasCountryCode) {
      // Format: +44xxxxxxxxxx (country code + number)
      // Must start with + and have at least 10 digits after country code
      const phoneRegex = /^\+[1-9]\d{9,14}$/;
      if (!phoneRegex.test(cleanPhone)) {
        setPhoneError('Please enter a valid international phone number (e.g., +44xxxxxxxxxx)');
        return false;
      }
    } else {
      // UK number without country code
      // Allow numbers starting with 0 (UK format) or 7 (mobile)
      // Should be 10-11 digits
      const ukPhoneRegex = /^(0|\+44)[0-9]{9,10}$/;
      if (!ukPhoneRegex.test(cleanPhone) && !/^[0-9]{10,11}$/.test(cleanPhone)) {
        setPhoneError('Please enter a valid UK phone number (10-11 digits, can start with 0)');
        return false;
      }
    }
    
    setPhoneError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const userAnswer = document.getElementById('captcha-input').value;
    
    if (userAnswer !== captchaAnswer) {
      alert('Incorrect captcha answer. Please try again.');
      return;
    }
    
    // Validate email
    if (!validateEmail(formData.email)) {
      return;
    }
    
    // Validate phone number if provided
    if (formData.phone && !validatePhoneNumber(formData.phone)) {
      return;
    }
    
    try {
      const response = await fetch(`https://formspree.io/f/${config.formspree.contactFormId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        alert('Thank you! We\'ll get back to you soon.');
        onClose();
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        alert('Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to send message. Please try again.');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear errors when user starts typing
    if (name === 'phone') {
      setPhoneError('');
    }
    if (name === 'email') {
      setEmailError('');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="contact-overlay" onClick={onClose}>
      <div className="contact-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>&times;</button>
        <h2>Contact Us</h2>
        <p className="contact-subtitle">We'd love to hear from you</p>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="Your name"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={() => validateEmail(formData.email)}
              required
              placeholder="Your email address"
              className={emailError ? 'error' : ''}
            />
            {emailError && <span className="error-message">{emailError}</span>}
          </div>
          
          <div className="form-group">
            <label htmlFor="phone">Phone Number</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              onBlur={() => validatePhoneNumber(formData.phone)}
              placeholder="Your phone number (e.g., +44xxxxxxxxxx or 0xxxxxxxxxx)"
              className={phoneError ? 'error' : ''}
            />
            {phoneError && <span className="error-message">{phoneError}</span>}
          </div>
          
          <div className="form-group">
            <label htmlFor="message">Message *</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              required
              rows="5"
              placeholder="Your message"
            ></textarea>
          </div>
          
          <div className="form-group captcha-group">
            <label htmlFor="captcha-input">What is {captcha}? *</label>
            <input
              type="text"
              id="captcha-input"
              required
              placeholder="Enter answer"
            />
          </div>
          
          <button type="submit" className="submit-btn">Send Message</button>
        </form>
      </div>
    </div>
  );
}
